<template>
    <div v-if="totalPages > 0 && hasVisibleControls" class="pdf-controls">
        <div class="pdf-controls__pagination">
            <div v-if="showZoom" class="pdf-controls__zoom-group">
                <CompassTooltip
                    :content="$t.__('common.actions.reduce')"
                    placement="top"
                    :arrow="false"
                >
                    <CompassIconButton
                        icon="minus"
                        variant="contrast"
                        :disabled="scale <= minZoom"
                        @click="zoomOut"
                    />
                </CompassTooltip>
                <div class="typographies-paragraph-small-regular">{{ scale }}%</div>
                <CompassTooltip
                    :content="$t.__('common.actions.expand')"
                    placement="top"
                    :arrow="false"
                >
                    <CompassIconButton
                        icon="plus"
                        variant="contrast"
                        :disabled="scale >= maxZoom"
                        @click="zoomIn"
                    />
                </CompassTooltip>
            </div>
            <div v-if="showDividerAfterZoom" class="pdf-controls__divider"></div>
            <div v-if="showPagination" class="pdf-controls__page-group">
                <CompassTooltip
                    :content="$t.__('common.labels.previous')"
                    placement="top"
                    :arrow="false"
                >
                    <CompassIconButton
                        icon="caret-left"
                        variant="contrast"
                        :disabled="currentPage === 1"
                        @click="goToPreviousPage"
                    />
                </CompassTooltip>
                <div class="typographies-paragraph-small-regular">
                    {{ currentPage }} / {{ totalPages }}
                </div>
                <CompassTooltip
                    :content="$t.__('common.labels.next')"
                    placement="top"
                    :arrow="false"
                >
                    <CompassIconButton
                        icon="caret-right"
                        variant="contrast"
                        :disabled="currentPage === totalPages"
                        @click="goToNextPage"
                    />
                </CompassTooltip>
            </div>
        </div>
        <div v-if="showDividerAfterPagination" class="pdf-controls__divider"></div>
        <CompassTooltip
            v-if="showExternalLink"
            :content="$t.__('common.labels.open_in_new_tab')"
            placement="top"
            :arrow="false"
        >
            <CompassIconButton icon="external-link" variant="contrast" @click="openPDFInNewTab" />
        </CompassTooltip>
    </div>
</template>

<script>
const ZOOM_CONFIG = {
    MIN: 10,
    MAX: 300,
    STEP: 10,
};

export default {
    props: {
        totalPages: {
            type: Number,
            default: 0,
        },
        pdfSrc: {
            type: String,
            required: true,
        },
        showZoom: {
            type: Boolean,
            default: false,
        },
        showPagination: {
            type: Boolean,
            default: false,
        },
        showExternalLink: {
            type: Boolean,
            default: false,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['scaleChanged', 'pageChanged'],
    data() {
        return {
            currentPage: 1,
            scale: 100,
            minZoom: ZOOM_CONFIG.MIN,
            maxZoom: ZOOM_CONFIG.MAX,
        };
    },
    computed: {
        hasVisibleControls() {
            return this.visible && (this.showZoom || this.showPagination || this.showExternalLink);
        },
        showDividerAfterZoom() {
            return this.showZoom && (this.showPagination || this.showExternalLink);
        },
        showDividerAfterPagination() {
            return this.showPagination && this.showExternalLink;
        },
    },
    methods: {
        zoomIn() {
            if (this.scale < ZOOM_CONFIG.MAX) {
                this.scale = Math.min(this.scale + ZOOM_CONFIG.STEP, ZOOM_CONFIG.MAX);
                this.$emit('scaleChanged', this.scale);
            }
        },
        zoomOut() {
            if (this.scale > ZOOM_CONFIG.MIN) {
                this.scale = Math.max(this.scale - ZOOM_CONFIG.STEP, ZOOM_CONFIG.MIN);
                this.$emit('scaleChanged', this.scale);
            }
        },
        goToPreviousPage() {
            if (this.currentPage > 1) {
                this.scrollToPage(this.currentPage - 1);
            }
        },
        goToNextPage() {
            if (this.currentPage < this.totalPages) {
                this.scrollToPage(this.currentPage + 1);
            }
        },
        scrollToPage(pageNum) {
            if (pageNum < 1 || pageNum > this.totalPages) {
                return;
            }
            this.$emit('pageChanged', pageNum);
        },
        openPDFInNewTab() {
            window.open(this.pdfSrc, '_blank');
        },
        // Use on PDFViewer.vue to update current page
        // eslint-disable-next-line vue/no-unused-properties
        updateCurrentPage(pageNum) {
            this.currentPage = pageNum;
        },
    },
};
</script>

<style lang="scss">
.pdf-controls {
    height: 50px;
    position: absolute;
    bottom: 150px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 5, 25, 0.9);
    color: var(--colors-common-text-standard-on-color);
    border-radius: 38px;
    padding: 0 var(--spacing-4);
    display: flex;
    align-items: center;
    gap: var(--spacing-3);
    transition: opacity 0.2s ease-in-out;
    z-index: 10;
    white-space: nowrap;

    &__divider {
        width: 1px;
        height: var(--spacing-3);
        background-color: var(--colors-common-border-default);
    }

    &__pagination {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
    }

    &__zoom-group,
    &__page-group {
        display: flex;
        align-items: center;
        gap: var(--spacing-0);
    }

    .compass-icon-button {
        border-radius: 50%;
    }
}
</style>
